import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { compressImageFile } from "@/lib/compressImageFile";
import { createMaterialItem, getApiErrorMessage, getMaterialItemFotoUrl, updateMaterialItem } from "@/lib/api";

const VACIO = { nombre: "", ubicacion: "", numero_serie: "", descripcion: "", notas: "" };

export default function ItemFormDialog({ open, onOpenChange, item, ubicaciones = [], onDone }) {
  const [form, setForm] = useState(VACIO);
  const [foto, setFoto] = useState(undefined); // undefined = sin cambios, "" = quitar, data URL = nueva
  const [procesando, setProcesando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(item ? { ...VACIO, ...Object.fromEntries(Object.keys(VACIO).map((k) => [k, item[k] || ""])) } : VACIO);
    setFoto(undefined);
    setSaving(false);
  }, [open, item]);

  const preview = foto !== undefined ? foto || null : item?.tiene_foto ? getMaterialItemFotoUrl(item.id, item.updated_at) : null;

  const cargarFoto = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Elige una imagen");
      return;
    }
    setProcesando(true);
    try {
      setFoto(await compressImageFile(file, { maxEdge: 1200, maxOutputBytes: 400 * 1024 }));
    } catch (e) {
      toast.error("No se pudo procesar la imagen");
    } finally {
      setProcesando(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (foto !== undefined) payload.foto = foto;
      if (item) await updateMaterialItem(item.id, payload);
      else await createMaterialItem(payload);
      toast.success(item ? "Equipo actualizado" : "Equipo añadido al inventario");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo guardar el equipo"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>{item ? "Editar equipo" : "Nuevo equipo"}</DialogTitle>
            <DialogDescription>Una buena foto y la ubicación ayudan a que todos lo encuentren y lo devuelvan a su sitio.</DialogDescription>
          </DialogHeader>

          <Separator className="-mx-6 w-auto" />

          <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  cargarFoto(e.dataTransfer.files?.[0]);
                }}
                className={cn(
                  "relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed text-muted-foreground transition-colors hover:border-primary hover:text-primary",
                  dragOver && "border-primary bg-primary/5"
                )}
              >
                {procesando ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : preview ? (
                  <img src={preview} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="flex flex-col items-center gap-1 px-3 text-center text-xs">
                    <ImagePlus className="h-7 w-7" strokeWidth={1.5} />
                    Arrastra una foto o haz clic
                  </span>
                )}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => cargarFoto(e.target.files?.[0])} />
              {preview && (
                <Button type="button" variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => setFoto("")}>
                  <Trash2 className="h-3.5 w-3.5" /> Quitar foto
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="mi-nombre">Nombre *</Label>
                <Input id="mi-nombre" value={form.nombre} onChange={set("nombre")} placeholder="Ej. Sony A7 IV #1" required autoFocus />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mi-ubic">Dónde se guarda</Label>
                <Input id="mi-ubic" list="mi-ubicaciones" value={form.ubicacion} onChange={set("ubicacion")} placeholder="Ej. Armario estudio, balda 2" />
                <datalist id="mi-ubicaciones">
                  {ubicaciones.map((u) => (
                    <option key={u} value={u} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mi-serie">Nº de serie</Label>
                <Input id="mi-serie" value={form.numero_serie} onChange={set("numero_serie")} />
              </div>
            </div>
          </div>

          <Separator className="-mx-6 w-auto" />

          <div className="space-y-1.5">
            <Label htmlFor="mi-desc">Descripción / qué incluye</Label>
            <Textarea id="mi-desc" rows={2} value={form.descripcion} onChange={set("descripcion")} placeholder="Ej. Cuerpo + 2 baterías + cargador + tarjeta 128 GB" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mi-notas">Notas internas</Label>
            <Textarea id="mi-notas" rows={2} value={form.notas} onChange={set("notas")} />
          </div>

          <DialogFooter className="-mx-6 border-t px-6 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || procesando || !form.nombre.trim()}>
              {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {item ? "Guardar" : "Añadir equipo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
